import { useEffect, useState } from 'react';

import {
  Box,
  Button,
  Paper,
  Popper,
  Portal,
  Stack,
  Typography,
} from '@mui/material';

import { useLocale } from '@shared/hooks/locale';

import type { DismissReason } from '../state/experienceState';

import type { TourStep } from './tourTypes';

const SPOTLIGHT_PADDING = 6;

type Rect = { top: number; left: number; width: number; height: number };

const sameRect = (a: Rect | null, b: Rect) =>
  a != null &&
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height;

type Props = {
  step: TourStep;
  index: number;
  count: number;
  // The step's element once it is on screen (after its reveal), else null.
  showAnchor: (anchor: string) => Promise<HTMLElement | null>;
  onBack: () => void;
  onNext: () => void;
  // The element is missing: skip this step.
  onUnavailable: () => void;
  onDismiss: (reason: DismissReason) => void;
};

export const TourOverlay = ({
  step,
  index,
  count,
  showAnchor,
  onBack,
  onNext,
  onUnavailable,
  onDismiss,
}: Props) => {
  const { t } = useLocale();
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const isLast = index + 1 >= count;

  useEffect(() => {
    let cancelled = false;
    let frame = 0;
    showAnchor(step.anchor)
      .then(anchor => {
        if (cancelled) {
          return;
        }
        if (anchor == null) {
          // Gone between two steps (the user navigated), or its panel did
          // not open.
          onUnavailable();
          return;
        }
        anchor.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        setElement(anchor);
        // The spotlight follows the anchor through scrolling, resizing and
        // layout shifts; the tour ends if the anchor leaves the page.
        const track = () => {
          if (!anchor.isConnected) {
            onDismiss('navigated_away');
            return;
          }
          const box = anchor.getBoundingClientRect();
          const next = {
            top: box.top,
            left: box.left,
            width: box.width,
            height: box.height,
          };
          setRect(previous => (sameRect(previous, next) ? previous : next));
          frame = requestAnimationFrame(track);
        };
        track();
      })
      .catch(() => onUnavailable());
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
    // The step is fixed for this component (keyed by step index).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss('escape');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  if (element == null || rect == null) {
    return null;
  }

  return (
    <>
      <Portal>
        <Box
          aria-hidden
          sx={theme => ({
            position: 'fixed',
            top: rect.top - SPOTLIGHT_PADDING,
            left: rect.left - SPOTLIGHT_PADDING,
            width: rect.width + SPOTLIGHT_PADDING * 2,
            height: rect.height + SPOTLIGHT_PADDING * 2,
            borderRadius: 2,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.45)',
            pointerEvents: 'none',
            zIndex: theme.zIndex.modal + 1,
            transition: 'all 200ms ease',
          })}
        />
      </Portal>
      <Popper
        open
        anchorEl={element}
        placement={step.placement ?? 'bottom'}
        modifiers={[
          { name: 'offset', options: { offset: [0, SPOTLIGHT_PADDING + 10] } },
          // `altAxis`: a step on a large element (the whole map) shows its
          // card inside the element instead of off screen.
          {
            name: 'preventOverflow',
            options: { padding: 16, altAxis: true },
          },
        ]}
        sx={theme => ({ zIndex: theme.zIndex.modal + 2 })}
      >
        <Paper
          role="dialog"
          aria-labelledby="tour-step-title"
          elevation={8}
          sx={{ p: 2, width: 320, maxWidth: 'calc(100vw - 32px)' }}
        >
          {count > 1 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('{{current}} of {{total}}', {
                current: index + 1,
                total: count,
              })}
            </Typography>
          )}
          <Typography
            id="tour-step-title"
            variant="subtitle1"
            sx={{ fontWeight: 600, mt: 0.5 }}
          >
            {step.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {step.body}
          </Typography>
          <Stack
            direction="row"
            sx={{
              mt: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Button
              size="small"
              color="inherit"
              onClick={() => onDismiss('skipped')}
            >
              {count === 1 ? t('Not now') : t('Skip tour')}
            </Button>
            <Stack direction="row" spacing={1}>
              {index > 0 && (
                <Button size="small" onClick={onBack}>
                  {t('Back')}
                </Button>
              )}
              <Button
                size="small"
                variant="contained"
                onClick={onNext}
                autoFocus
              >
                {count === 1 ? t('Got it') : isLast ? t('Done') : t('Next')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Popper>
    </>
  );
};
